import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { apiConfigService } from '../services/api';
import '../styles/Navbar.css';

function NavDropdown({ group, isPathActive, onNavigate }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const hasActiveChild = group.items.some((item) => isPathActive(item.path));

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  if (group.items.length === 1) {
    const item = group.items[0];
    return (
      <Link
        to={item.path}
        className={`nav-link ${isPathActive(item.path) ? 'active' : ''}`}
        onClick={onNavigate}
      >
        {item.icon && <span className="nav-link-icon" aria-hidden>{item.icon}</span>}
        <span className="nav-link-label">{item.label}</span>
      </Link>
    );
  }

  return (
    <div className={`nav-dropdown ${open ? 'open' : ''} ${hasActiveChild ? 'has-active' : ''}`} ref={ref}>
      <button
        type="button"
        className={`nav-dropdown-trigger ${hasActiveChild ? 'active' : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="true"
      >
        {group.icon && <span className="nav-link-icon" aria-hidden>{group.icon}</span>}
        <span className="nav-link-label">{group.label}</span>
        <span className="nav-chevron" aria-hidden>▾</span>
      </button>
      <div className="nav-dropdown-panel" role="menu">
        {group.items.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            className={`nav-dropdown-item ${isPathActive(item.path) ? 'active' : ''}`}
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onNavigate?.();
            }}
          >
            {item.icon && <span className="nav-dropdown-item-icon" aria-hidden>{item.icon}</span>}
            <span>{item.label}</span>
          </Link>
        ))}
      </div>
    </div>
  );
}

const Navbar = () => {
  const [logoUrl, setLogoUrl] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, logout, isAdminOrDiretor } = useAuth();
  const { toggleTheme, isDark } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await apiConfigService.refreshLogoVersion();
      if (!cancelled) setLogoUrl(apiConfigService.getLogoUrl());
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isPathActive = useCallback(
    (path) => {
      if (location.pathname === path) return true;
      if (path === '/dashboard' || path === '/vendas') return false;
      return location.pathname.startsWith(`${path}/`);
    },
    [location.pathname]
  );

  const navGroups = useMemo(() => {
    if (!user) return [];

    const groups = [];

    groups.push({
      id: 'inicio',
      label: 'Início',
      icon: '⌂',
      items: [{ path: '/dashboard', label: 'Dashboard', icon: '⌂' }]
    });

    const vendasItems = [{ path: '/vendas', label: 'Lista de vendas', icon: '▤' }];
    if (user.role !== 'backoffice' && user.role !== 'supervisor_bko') {
      vendasItems.push({ path: '/vendas/consulta-viabilidade', label: 'Nova venda', icon: '＋' });
    }
    if (isAdminOrDiretor() || user.role === 'backoffice' || user.role === 'supervisor_bko') {
      vendasItems.push({ path: '/fila-tratamento', label: 'Fila de tratamento', icon: '☰' });
    }
    if (isAdminOrDiretor() || user.role === 'supervisor' || user.role === 'vendedor') {
      vendasItems.push({ path: '/vendas-pagamento', label: 'Controle de pagamentos', icon: '₿' });
    }
    if (vendasItems.length > 0) {
      groups.push({ id: 'vendas', label: 'Vendas', icon: '▤', items: vendasItems });
    }

    const recursosItems = [];
    if (isAdminOrDiretor()) {
      recursosItems.push({ path: '/planos', label: 'Planos', icon: '▦' });
    }
    if (isAdminOrDiretor() || user.role === 'supervisor' || user.role === 'vendedor') {
      recursosItems.push({ path: '/arquivos', label: 'Arquivos', icon: '▣' });
      recursosItems.push({ path: '/catalogo-planos', label: 'Catálogo de planos', icon: '▧' });
    }
    if (recursosItems.length > 0) {
      groups.push({ id: 'recursos', label: 'Recursos', icon: '▣', items: recursosItems });
    }

    const consultasItems = [
      { path: '/infospy', label: 'InfoSpy', icon: '◎' },
      { path: '/infospypj', label: 'InfoSpyPj', icon: '◈' }
    ];
    if (isAdminOrDiretor() || user.acesso_consulta_cadastral) {
      consultasItems.push({ path: '/consulta-cadastral', label: 'Consulta cadastral', icon: '▤' });
    }
    if (isAdminOrDiretor() || user.acesso_como_funciona) {
      consultasItems.push({ path: '/did', label: 'DID', icon: '☎' });
    }
    groups.push({ id: 'consultas', label: 'Consultas', icon: '◎', items: consultasItems });

    if (isAdminOrDiretor()) {
      groups.push({
        id: 'admin',
        label: 'Admin',
        icon: '⚙',
        items: [
          { path: '/usuarios', label: 'Usuários', icon: '👤' },
          { path: '/config-api', label: 'Configurações API', icon: '⚙' },
          { path: '/automacao/validacao-ok', label: 'Automação NEO', icon: '🤖' }
        ]
      });
    }

    return groups.filter((g) => g.items.length > 0);
  }, [user, isAdminOrDiretor]);

  const closeMobile = () => setMobileOpen(false);

  return (
    <nav className="navbar">
      <div className="navbar-container">
        <Link to="/dashboard" className="navbar-logo" onClick={closeMobile}>
          {logoUrl ? (
            <img
              src={logoUrl}
              alt="EXTK e ONDA TELECOM"
              className="navbar-logo-img"
              decoding="async"
              onError={() => setLogoUrl('/logo-extk-onda.png')}
            />
          ) : (
            <>
              <h2>EXTK</h2>
              <span className="navbar-subtitle">e ONDA TELECOM</span>
            </>
          )}
        </Link>

        <div className="navbar-menu navbar-menu--desktop">
          {navGroups.map((group) => (
            <NavDropdown
              key={group.id}
              group={group}
              isPathActive={isPathActive}
            />
          ))}
        </div>

        <div className="navbar-actions">
          <button
            type="button"
            className="theme-toggle"
            onClick={toggleTheme}
            title={isDark ? 'Mudar para tema claro' : 'Mudar para tema escuro'}
            aria-label={isDark ? 'Ativar tema claro' : 'Ativar tema escuro'}
          >
            <span className="theme-toggle-icon" aria-hidden>{isDark ? '☀' : '☾'}</span>
            <span className="theme-toggle-label">{isDark ? 'Claro' : 'Escuro'}</span>
          </button>
          <div className="navbar-user navbar-user--desktop">
            <div className="user-info">
              <span className="user-name">{user?.nome}</span>
              <span className="user-role">{user?.role?.toUpperCase()}</span>
            </div>
            <button type="button" onClick={handleLogout} className="btn-logout">
              Sair
            </button>
          </div>

          <button
            type="button"
            className={`navbar-toggle ${mobileOpen ? 'open' : ''}`}
            onClick={() => setMobileOpen((v) => !v)}
            aria-label={mobileOpen ? 'Fechar menu' : 'Abrir menu'}
            aria-expanded={mobileOpen}
          >
            <span />
            <span />
            <span />
          </button>
        </div>
      </div>

      <div
        className={`navbar-mobile-overlay ${mobileOpen ? 'visible' : ''}`}
        onClick={closeMobile}
        aria-hidden={!mobileOpen}
      />

      <aside className={`navbar-mobile-drawer ${mobileOpen ? 'open' : ''}`} aria-hidden={!mobileOpen}>
        <div className="navbar-mobile-header">
          <span className="navbar-mobile-title">Menu</span>
          <button type="button" className="navbar-mobile-close" onClick={closeMobile} aria-label="Fechar">
            ×
          </button>
        </div>

        <div className="navbar-mobile-user">
          <span className="user-name">{user?.nome}</span>
          <span className="user-role">{user?.role?.toUpperCase()}</span>
        </div>

        <nav className="navbar-mobile-nav">
          {navGroups.map((group) => (
            <div key={group.id} className="navbar-mobile-group">
              {group.items.length > 1 && (
                <div className="navbar-mobile-group-label">{group.label}</div>
              )}
              {group.items.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`navbar-mobile-link ${isPathActive(item.path) ? 'active' : ''}`}
                  onClick={closeMobile}
                >
                  {item.icon && <span className="nav-link-icon" aria-hidden>{item.icon}</span>}
                  {item.label}
                </Link>
              ))}
            </div>
          ))}
        </nav>

        <button
          type="button"
          className="theme-toggle theme-toggle--mobile"
          onClick={toggleTheme}
          style={{ margin: '0 1rem 0.75rem', width: 'calc(100% - 2rem)' }}
        >
          <span className="theme-toggle-icon" aria-hidden>{isDark ? '☀' : '☾'}</span>
          <span>{isDark ? 'Tema claro' : 'Tema escuro'}</span>
        </button>
        <button type="button" className="btn-logout btn-logout--mobile" onClick={handleLogout}>
          Sair
        </button>
      </aside>
    </nav>
  );
};

export default Navbar;
